import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TabbedPageCard from "../../components/common/TabbedPageCard";
import PageTitle from "../../components/common/PageTitle";
import { PtiIcon } from "../../components/common/pageIcons";
import PageLayout from "../../components/layout/PageLayout";
import PtiManagementList from "./components/PtiManagementList";
import PtiComplianceList from "./components/PtiComplianceList";
import { PTI_COMPLIANCE_STATUS_FILTER_OPTIONS } from "./ptiConstants";

const PTI_TAB_INDEX: Record<string, number> = {
    vehicles: 0,
    trailers: 1,
    compliance: 2,
};

const COMPLIANCE_STATUSES = new Set(
    PTI_COMPLIANCE_STATUS_FILTER_OPTIONS.map((option) => option.value),
);

const COMPLIANCE_QUERY_KEYS = [
    "status",
    "driver",
    "vehicle",
    "trailer",
    "inspection",
];

const parsePositiveId = (value: string | null) => {
    if (!value) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const PtiPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(() => {
        const tab = searchParams.get("tab");
        return tab && tab in PTI_TAB_INDEX ? PTI_TAB_INDEX[tab] : 0;
    });

    const complianceQuery = useMemo(() => {
        const status = searchParams.get("status");
        return {
            status:
                status && COMPLIANCE_STATUSES.has(status) ? status : undefined,
            driverUserId: parsePositiveId(searchParams.get("driver")),
            vehicleId: parsePositiveId(searchParams.get("vehicle")),
            trailerId: parsePositiveId(searchParams.get("trailer")),
            inspectionId: parsePositiveId(searchParams.get("inspection")),
        };
    }, [searchParams]);

    const handleTabChange = (index: number) => {
        setActiveTab(index);
        const next = new URLSearchParams(searchParams);
        if (index === 2) {
            next.set("tab", "compliance");
        } else if (index === 1) {
            next.set("tab", "trailers");
            COMPLIANCE_QUERY_KEYS.forEach((key) => next.delete(key));
        } else {
            next.delete("tab");
            COMPLIANCE_QUERY_KEYS.forEach((key) => next.delete(key));
        }
        setSearchParams(next, { replace: true });
    };

    return (
        <PageLayout>
            <PageTitle
                title="Pre-Trip Inspections"
                icon={<PtiIcon />}
                titleVariant="h4"
                subtitle="Review driver submissions, add measurements, and approve PTIs"
            />

            <TabbedPageCard
                tabs={[
                    { label: "Vehicles", content: <PtiManagementList assetType="Vehicle" /> },
                    { label: "Trailers", content: <PtiManagementList assetType="Trailer" /> },
                    {
                        label: "Compliance",
                        content: (
                            <PtiComplianceList
                                initialStatus={complianceQuery.status}
                                initialDriverUserId={complianceQuery.driverUserId}
                                initialVehicleId={complianceQuery.vehicleId}
                                initialTrailerId={complianceQuery.trailerId}
                                initialInspectionId={complianceQuery.inspectionId}
                            />
                        ),
                    },
                ]}
                selectedTab={activeTab}
                onTabChange={handleTabChange}
            />
        </PageLayout>
    );
};

export default PtiPage;
